import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, PackageOpen, MessageCircle, Twitter, Facebook, Copy, Check, Layers } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import api from "../lib/api";

export default function CollectionsPage() {
    const { id } = useParams();
    const [copied, setCopied] = useState(false);
    const { data, isLoading, isError } = useQuery({
        queryKey: ["collection", id],
        queryFn: async () => (await api.get(`/collections/${id}`)).data,
        retry: false,
    });

    const shareUrl = `${window.location.origin}/koleksi/${id}`;
    const shareText = data ? `${data.title} — koleksi kurasi Acture.id` : "Koleksi kurasi Acture.id";

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            toast.success("Link koleksi dicopy!");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Gagal copy link");
        }
    };

    return (
        <>
            <Navbar />
            <main className="pt-28 md:pt-36 pb-16 md:pb-24 min-h-[70vh]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link
                        to="/"
                        data-testid="collection-back-button"
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-brand-ink bg-white font-bold text-sm shadow-hard-sm hover:-translate-y-0.5 hover:shadow-hard transition-all active:translate-y-0"
                    >
                        <ArrowLeft className="w-4 h-4" strokeWidth={2.6} /> Kembali ke Beranda
                    </Link>

                    {isLoading && <div className="mt-8 h-44 rounded-[26px] border-2 border-brand-ink/10 bg-white animate-pulse" />}

                    {!isLoading && isError && (
                        <div className="mt-8 rounded-[26px] border-2 border-dashed border-brand-ink/40 bg-white/70 py-16 text-center">
                            <PackageOpen className="w-10 h-10 mx-auto text-brand-ink/50" />
                            <p className="mt-4 font-display text-xl font-bold">Koleksi tidak ditemukan</p>
                            <p className="mt-1.5 text-sm text-slate-500">Mungkin koleksinya sudah dihapus kuratornya.</p>
                        </div>
                    )}

                    {data && (
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-8 rounded-[30px] border-2 border-brand-ink bg-brand-forest text-brand-cream p-8 md:p-10 shadow-hard relative overflow-hidden"
                        >
                            <Layers className="absolute top-8 right-8 w-14 h-14 text-brand-cream/15" strokeWidth={2} aria-hidden="true" />
                            <span className="inline-block px-3 py-1 rounded-full border-2 border-brand-ink bg-brand-sunshine text-brand-ink text-[11px] font-extrabold uppercase tracking-[0.18em]">
                                Koleksi Kurasi
                            </span>
                            <h1 data-testid="collection-title" className="mt-4 font-display text-3xl md:text-5xl font-extrabold tracking-tight">
                                {data.title}
                            </h1>
                            {data.description && <p className="mt-3 text-brand-cream/85 max-w-2xl leading-relaxed">{data.description}</p>}
                            <div className="mt-6 flex flex-wrap items-center gap-2.5">
                                <span className="text-xs font-bold text-brand-cream/70 mr-1">{data.products.length} karya —</span>
                                <a
                                    data-testid="collection-share-whatsapp"
                                    href={`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${shareUrl}`)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-brand-ink bg-brand-wa text-white text-xs font-bold shadow-hard-sm hover:-translate-y-0.5 transition-all"
                                >
                                    <MessageCircle className="w-3.5 h-3.5" strokeWidth={2.6} /> WhatsApp
                                </a>
                                <a
                                    data-testid="collection-share-x"
                                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-brand-ink bg-brand-cream text-brand-ink text-xs font-bold shadow-hard-sm hover:-translate-y-0.5 transition-all"
                                >
                                    <Twitter className="w-3.5 h-3.5" strokeWidth={2.4} /> X
                                </a>
                                <a
                                    data-testid="collection-share-facebook"
                                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-brand-ink bg-brand-cream text-brand-ink text-xs font-bold shadow-hard-sm hover:-translate-y-0.5 transition-all"
                                >
                                    <Facebook className="w-3.5 h-3.5" strokeWidth={2.4} /> Facebook
                                </a>
                                <button
                                    data-testid="collection-share-copy"
                                    onClick={copy}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 border-brand-ink bg-brand-sunshine text-brand-ink text-xs font-bold shadow-hard-sm hover:-translate-y-0.5 transition-all"
                                >
                                    {copied ? <Check className="w-3.5 h-3.5" strokeWidth={2.8} /> : <Copy className="w-3.5 h-3.5" strokeWidth={2.4} />}
                                    {copied ? "Tercopy!" : "Copy Link"}
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {data && data.products.length > 0 && (
                        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7 items-stretch">
                            {data.products.map((p, i) => (
                                <ProductCard key={p.id} product={p} index={i} />
                            ))}
                        </div>
                    )}

                    {data && data.products.length === 0 && (
                        <div className="mt-10 rounded-[26px] border-2 border-dashed border-brand-ink/40 bg-white/70 py-16 text-center">
                            <PackageOpen className="w-10 h-10 mx-auto text-brand-ink/50" />
                            <p className="mt-4 font-display text-xl font-bold">Belum ada karya di koleksi ini</p>
                        </div>
                    )}
                </div>
            </main>
            <Footer />
        </>
    );
}
